import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { callAPI } from '../../utils/apiUtils';
import { apiUrls } from '../../utils/apiUrls';
import {
  getGuestCart,
  saveGuestCart,
  clearGuestCart,
  calculateGuestCartSummary
} from '../common/guestCart';

const isUserLoggedIn = (state) => {
  return state.auth?.isAuthenticated || !!state.auth?.token;
};

export const fetchCart = createAsyncThunk(
  'cart/fetchCart',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const isLoggedIn = isUserLoggedIn(state);

      if (isLoggedIn) {
        const response = await callAPI(
          apiUrls.getCartList,
          {},
          "GET",
          {}
        );

        if (response?.data?.success) {
          const apiData = response.data.data;
          const items = apiData.data || [];

          const mappedItems = items.map(item => {
            const tire_detail_id = item.tire_detail_id;
            const battery_detail_id = item.battery_detail_id;
            const windshield_detail_id = item.windshield_detail_id;

            const variantId = tire_detail_id || battery_detail_id || windshield_detail_id;

            let price = 0;
            let originalPrice = 0;
            let image = "";
            let stock = 0;  // 👈 Add stock variable

            if (tire_detail_id && item.product?.tyre_variants) {
              const variant = item.product.tyre_variants.find(v => v.id == tire_detail_id);
              if (variant) {
                price = parseFloat(variant.discounted_price || variant.price || 0);
                originalPrice = parseFloat(variant.price || 0);
                stock = parseInt(variant.stock) || 0;  // 👈 Get stock from variant
                if (variant.images && variant.images.length > 0) {
                  image = variant.images[0].image_url;
                }
              }
            }
            else if (battery_detail_id && item.product?.battery_variants) {
              const variant = item.product.battery_variants.find(v => v.id == battery_detail_id);
              if (variant) {
                price = parseFloat(variant.discounted_price || variant.price || 0);
                originalPrice = parseFloat(variant.price || 0);
                stock = parseInt(variant.stock) || 0;  // 👈 Get stock from variant
                if (variant.images && variant.images.length > 0) {
                  image = variant.images[0].image_url;
                }
              }
            }
            else if (windshield_detail_id && item.product?.windshield_variants) {
              const variant = item.product.windshield_variants.find(v => v.id == windshield_detail_id);
              if (variant) {
                price = parseFloat(variant.discounted_price || variant.price || 0);
                originalPrice = parseFloat(variant.price || 0);
                stock = parseInt(variant.stock) || 0;  // 👈 Get stock from variant
                if (variant.images && variant.images.length > 0) {
                  image = variant.images[0].image_url;
                }
              }
            }

            if (price == 0 && item.total_price) {
              price = parseFloat(item.total_price);
            }

            let size = null;
            if (item.product?.tyre_variants?.length > 0 && tire_detail_id) {
              const variant = item.product.tyre_variants.find(v => v.id == tire_detail_id);
              size = variant?.size || null;
            }

            return {
              id: item.id,
              productId: item.product_id,
              variantId: variantId,
              tire_detail_id: tire_detail_id,
              battery_detail_id: battery_detail_id,
              windshield_detail_id: windshield_detail_id,
              name: item.product?.name || 'Product',
              image: image,
              quantity: item.quantity,
              price: price,
              originalPrice: originalPrice,
              brand: item.product?.brand?.name,
              category: item.product?.category?.title,
              category_id: item.category_id,
              size: size,
              is_saved: item.is_saved,
              created_at: item.created_at,
              updated_at: item.updated_at,
              stock: stock,  // 👈 Add stock to returned item
              maxQuantity: stock  // 👈 Also add maxQuantity for easy access
            };
          });

          const subtotal = mappedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
          const itemCount = mappedItems.reduce((sum, item) => sum + item.quantity, 0);

          return {
            type: 'user',
            data: {
              items: mappedItems,
              total_count: itemCount,
              subtotal: subtotal,
              total_amount: subtotal,
              pagination: {
                current_page: apiData.current_page,
                last_page: apiData.last_page,
                per_page: apiData.per_page,
                total: apiData.total
              }
            }
          };
        }
        return rejectWithValue(response?.data?.message || 'Failed to fetch cart');
      } else {
        const guestCart = getGuestCart();
        const summary = calculateGuestCartSummary(guestCart.items);

        return {
          type: 'guest',
          data: {
            items: guestCart.items || [],
            total_count: guestCart.items.length,
            subtotal: summary.subtotal,
            total_amount: summary.total
          }
        };
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch cart');
    }
  }
);

export const addToCart = createAsyncThunk(
  'cart/addToCart',
  async (productData, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const isLoggedIn = isUserLoggedIn(state);

      if (isLoggedIn) {
        const payload = {
          product_id: productData.product_id || productData.productId,
          tire_detail_id: productData.tire_detail_id || null,
          battery_detail_id: productData.battery_detail_id || null,
          windshield_detail_id: productData.windshield_detail_id || null,
          quantity: productData.quantity || 1
        };

        const response = await callAPI(
          apiUrls.addToCart, {}, 'POST',
          payload,

        );

        if (response?.data?.success) {
          const data = response.data.data;
          const itemsCount = data.items?.length || 0;

          return {
            type: 'user',
            data: {
              ...data,
              total_count: itemsCount
            },
            action: 'add'
          };
        }
        return rejectWithValue(response?.data?.message || 'Failed to add to cart');
      } else {
        const guestCart = getGuestCart();
        const items = [...guestCart.items];
        const variant_id = productData.tire_detail_id || productData.battery_detail_id || productData.windshield_detail_id;
        const existingIndex = items.findIndex(
          item =>
            item.productId == productData.product_id &&
            item.variant_id == variant_id
        );
        if (existingIndex >= 0) {
          items[existingIndex].quantity += productData.quantity;
          items[existingIndex].price = productData.price;
          items[existingIndex].originalPrice = productData.originalPrice;
        } else {
          const newItem = {
            id: Date.now() + Math.random(),
            productId: productData.product_id,
            variant_id: variant_id,
            tire_detail_id: productData.tire_detail_id || null,
            battery_detail_id: productData.battery_detail_id || null,
            windshield_detail_id: productData.windshield_detail_id || null,
            name: productData.name,
            brand: productData.brand,
            category: productData.category,
            category_id: productData.category_id,
            price: productData.price,
            originalPrice: productData.originalPrice,
            image: productData.image,
            size: productData.size,
            capacity: productData.capacity,
            voltage: productData.voltage,
            material: productData.material,
            glassType: productData.glassType,
            quantity: parseInt(productData.quantity),
            variant_details: productData.variant_details || {},
            maxQuantity: productData.stock_quantity || 10,
          };
          console.log('Adding new guest item with REAL data:', newItem);
          items.push(newItem);
        }

        saveGuestCart(items);

        const summary = calculateGuestCartSummary(items);

        return {
          type: 'guest',
          data: {
            items,
            total_count: items.length,
            subtotal: summary.subtotal,
            total_amount: summary.total
          },
          action: 'add'
        };
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add to cart');
    }
  }
);

export const createCheckoutSession = createAsyncThunk(
  'cart/createCheckoutSession',
  async (checkoutData, { rejectWithValue }) => {
    try {
      const response = await callAPI(
        apiUrls.checkoutItems,
        {},
        "POST",
        checkoutData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createPayPalCheckoutSession = createAsyncThunk(
  'cart/createPayPalCheckoutSession',
  async ({ checkoutId, successUrl, cancelUrl }, { rejectWithValue }) => {
    try {
      const response = await callAPI(
        `${apiUrls.paypalSession}/${checkoutId}`,
        {},
        "POST",
        {
          success_url: successUrl,
          cancel_url: cancelUrl
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const verifyPayPalPayment = createAsyncThunk(
  'cart/verifyPayPalPayment',
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await callAPI(
        `${apiUrls.checkoutPaypalVerify}?order_id=${orderId}`,
        {},
        "GET"
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateCartItem = createAsyncThunk(
  'cart/updateCartItem',
  async ({ itemId, quantity }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const isLoggedIn = isUserLoggedIn(state);
      const currentItem = state.cart.cartItems.find(item => item.id == itemId);
      if (currentItem && quantity > (currentItem.maxQuantity || 999)) {
        return rejectWithValue(`Only ${currentItem.maxQuantity} items available in stock`);
      }
      if (isLoggedIn) {
        const response = await callAPI(
          apiUrls.updateCartItem,
          {
            cart_id: itemId,
            quantity: quantity
          },
          "POST",
          {}
        );

        if (response?.data?.success) {
          return {
            type: 'user',
            data: { itemId, quantity }
          };
        }
        return rejectWithValue(response?.data?.message || 'Failed to update cart');
      } else {
        const guestCart = getGuestCart();
        const items = guestCart.items.map(item => {
          if (item.id == itemId) {
            const maxQty = item.maxQuantity || 999;
            const newQuantity = Math.min(Math.max(1, quantity), maxQty);
            return { ...item, quantity: newQuantity };
          }
          return item;
        });

        saveGuestCart(items);
        const summary = calculateGuestCartSummary(items);

        return {
          type: 'guest',
          data: {
            items,
            itemId,
            quantity: Math.min(Math.max(1, quantity), items.find(i => i.id == itemId)?.maxQuantity || 999),
            total_count: items.length,
            subtotal: summary.subtotal,
            total_amount: summary.total
          }
        };
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update cart');
    }
  }
);

export const removeFromCart = createAsyncThunk(
  'cart/removeFromCart',
  async (itemId, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const isLoggedIn = isUserLoggedIn(state);

      if (isLoggedIn) {
        const response = await callAPI(
          apiUrls.removeFromCart,
          { cart_id: itemId },
          "POST",
          {}
        );

        if (response?.data?.success) {
          return {
            type: 'user',
            data: itemId
          };
        }
        return rejectWithValue(response?.data?.message || 'Failed to remove from cart');
      } else {
        const guestCart = getGuestCart();
        const items = guestCart.items.filter(item => item.id !== itemId);

        saveGuestCart(items);
        const summary = calculateGuestCartSummary(items);

        return {
          type: 'guest',
          data: {
            items,
            itemId,
            total_count: items.length,
            subtotal: summary.subtotal,
            total_amount: summary.total
          }
        };
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to remove from cart');
    }
  }
);

export const clearCart = createAsyncThunk(
  'cart/clearCart',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const isLoggedIn = isUserLoggedIn(state);

      if (isLoggedIn) {
        const response = await callAPI(
          apiUrls.clearCart,
          {},
          "POST",
          {}
        );

        if (response?.data?.success) {
          return {
            type: 'user'
          };
        }
        return rejectWithValue(response?.data?.message || 'Failed to clear cart');
      } else {
        clearGuestCart();

        return {
          type: 'guest'
        };
      }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to clear cart');
    }
  }
);

export const mergeCartAfterLogin = createAsyncThunk(
  'cart/mergeCartAfterLogin',
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState();
      const guestCart = getGuestCart();

      if (guestCart.items.length == 0) {
        return { merged: false, message: 'No guest items to merge' };
      }

      const mergePromises = guestCart.items.map(item =>
        callAPI(
          apiUrls.addToCart,
          {
            product_id: item.productId,
            variant_id: item.variant_id,
            quantity: item.quantity
          },
          "POST",
          {}
        )
      );

      await Promise.all(mergePromises);

      clearGuestCart();

      await dispatch(fetchCart());

      return { merged: true, count: guestCart.items.length };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to merge cart');
    }
  }
);

const cartSlice = createSlice({
  name: 'cart',
  initialState: {
    cartItems: [],
    cartType: 'guest',

    subtotal: 0,
    discounts: 0,
    shipping: 0,
    tax: 0,
    taxRate: 0.08,
    total: 0,
    itemCount: 0,

    appliedCoupon: null,
    couponDiscount: 0,

    loading: false,
    addLoading: false,
    updateLoading: false,
    removeLoading: false,
    couponLoading: false,
    shippingLoading: false,
    error: null,

    addSuccess: false,
    updateSuccess: false,
    removeSuccess: false,
    clearSuccess: false,
    couponSuccess: false,

    shippingEstimate: {
      cost: 0,
      days: null
    }
  },
  reducers: {
    optimisticAddToCart: (state, action) => {
      const { product, quantity = 1, size } = action.payload;

      const existingItemIndex = state.cartItems.findIndex(
        item => item.productId == product.id && item.size == size
      );

      if (existingItemIndex >= 0) {
        state.cartItems[existingItemIndex].quantity += quantity;
      } else {
        const newItem = {
          id: Date.now(),
          productId: product.id,
          name: product.name,
          image: product.image || "/image/item-1.png",
          quantity,
          size: size || "205/55R16",
          price: product.salePrice || product.price,
          originalPrice: product.price,
          maxQuantity: 10,
          brand: product.brand,
          inStock: true
        };
        state.cartItems.push(newItem);
        state.itemCount += 1;
      }

      const summary = calculateGuestCartSummary(state.cartItems);
      state.subtotal = summary.subtotal;
      state.itemCount = summary.itemCount;
      state.total = summary.total;
    },

    optimisticUpdateQuantity: (state, action) => {
      const { itemId, quantity } = action.payload;
      const item = state.cartItems.find(item => item.id == itemId);

      if (item) {
        item.quantity = Math.max(1, Math.min(quantity, item.maxQuantity || 99));
      }

      const summary = calculateGuestCartSummary(state.cartItems);
      state.subtotal = summary.subtotal;
      state.itemCount = summary.itemCount;
      state.total = summary.total;
    },

    optimisticRemoveItem: (state, action) => {
      state.cartItems = state.cartItems.filter(item => item.id !== action.payload);

      const summary = calculateGuestCartSummary(state.cartItems);
      state.subtotal = summary.subtotal;
      state.itemCount = summary.itemCount;
      state.total = summary.total;
    },

    optimisticClearCart: (state) => {
      state.cartItems = [];
      state.subtotal = 0;
      state.discounts = 0;
      state.shipping = 0;
      state.tax = 0;
      state.total = 0;
      state.itemCount = 0;
      state.appliedCoupon = null;
      state.couponDiscount = 0;
    },

    calculateCartSummary: (state) => {
      const summary = calculateGuestCartSummary(state.cartItems);
      state.subtotal = summary.subtotal;
      state.itemCount = state.cartItems.length;

      state.discounts = state.subtotal > 200 ? 20 : 0;
      state.shipping = state.subtotal > 100 ? 0 : 15;
      state.tax = state.subtotal * state.taxRate;
      state.tax = Number(state.tax.toFixed(2));
      state.total = state.subtotal - state.discounts - state.couponDiscount + state.shipping + state.tax;
      state.total = Number(state.total.toFixed(2));
    },

    clearCartError: (state) => {
      state.error = null;
    },

    resetCartState: (state) => {
      state.cartItems = [];
      state.subtotal = 0;
      state.discounts = 0;
      state.shipping = 0;
      state.tax = 0;
      state.total = 0;
      state.itemCount = 0;
      state.appliedCoupon = null;
      state.couponDiscount = 0;
      state.loading = false;
      state.error = null;
    },

    resetAddSuccess: (state) => {
      state.addSuccess = false;
    },

    resetUpdateSuccess: (state) => {
      state.updateSuccess = false;
    },

    resetRemoveSuccess: (state) => {
      state.removeSuccess = false;
    },

    resetClearSuccess: (state) => {
      state.clearSuccess = false;
    },

    resetCouponSuccess: (state) => {
      state.couponSuccess = false;
    },

    setTaxRate: (state, action) => {
      state.taxRate = action.payload;
      state.tax = state.subtotal * state.taxRate;
      state.tax = Number(state.tax.toFixed(2));
      state.total = state.subtotal - state.discounts - state.couponDiscount + state.shipping + state.tax;
      state.total = Number(state.total.toFixed(2));
    },

    setShipping: (state, action) => {
      state.shipping = Number(action.payload.toFixed(2));
      state.total = state.subtotal - state.discounts - state.couponDiscount + state.shipping + state.tax;
      state.total = Number(state.total.toFixed(2));
    },

    removeCouponLocal: (state) => {
      state.appliedCoupon = null;
      state.couponDiscount = 0;

      const summary = calculateGuestCartSummary(state.cartItems);
      state.subtotal = summary.subtotal;
      state.itemCount = state.cartItems.length;
      state.discounts = state.subtotal > 200 ? 20 : 0;
      state.shipping = state.subtotal > 100 ? 0 : 15;
      state.tax = state.subtotal * state.taxRate;
      state.tax = Number(state.tax.toFixed(2));
      state.total = state.subtotal - state.discounts - state.couponDiscount + state.shipping + state.tax;
      state.total = Number(state.total.toFixed(2));
    },

    setCartType: (state, action) => {
      state.cartType = action.payload;
    },

    clearGuestCartState: (state) => {
      if (state.cartType == 'guest') {
        state.cartItems = [];
        state.itemCount = 0;
        state.subtotal = 0;
        state.total = 0;
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCart.fulfilled, (state, action) => {
        state.loading = false;
        state.cartType = action.payload.type;

        if (action.payload.type == 'user') {
          const data = action.payload.data;
          state.cartItems = data.items || [];
          state.itemCount = data.total_count || data.items?.length || 0;
          state.subtotal = data.subtotal || 0;
          state.total = data.total_amount || 0;
        } else {
          const data = action.payload.data;
          state.cartItems = data.items || [];
          state.itemCount = data.total_count || data.items?.length || 0;
          state.subtotal = data.subtotal || 0;
          state.total = data.total_amount || 0;
        }
      })
      .addCase(fetchCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch cart';
      })

      .addCase(addToCart.pending, (state) => {
        state.addLoading = true;
        state.addSuccess = false;
        state.error = null;
      })
      .addCase(addToCart.fulfilled, (state, action) => {
        state.addLoading = false;
        state.addSuccess = true;

        if (action.payload.type == 'user') {
          const data = action.payload.data;
          if (data.items) {
            state.cartItems = data.items;
            state.itemCount = data.total_count || data.items?.length || 0;
            state.subtotal = data.subtotal || 0;
            state.total = data.total_amount || 0;
          }
        } else {
          const data = action.payload.data;
          state.cartItems = data.items;
          state.itemCount = data.total_count;
          state.subtotal = data.subtotal;
          state.total = data.total_amount;
        }
      })
      .addCase(addToCart.rejected, (state, action) => {
        state.addLoading = false;
        state.addSuccess = false;
        state.error = action.payload || 'Failed to add to cart';
      })

      .addCase(updateCartItem.pending, (state) => {
        state.updateLoading = true;
        state.updateSuccess = false;
        state.error = null;
      })
      .addCase(updateCartItem.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.updateSuccess = true;

        if (action.payload.type == 'user') {
          const { itemId, quantity } = action.payload.data;
          const item = state.cartItems.find(item => item.id == itemId);
          if (item) {
            item.quantity = quantity;
          }
        } else {
          const data = action.payload.data;
          state.cartItems = data.items;
          state.itemCount = data.total_count;
          state.subtotal = data.subtotal;
          state.total = data.total_amount;
        }
      })
      .addCase(updateCartItem.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateSuccess = false;
        state.error = action.payload || 'Failed to update cart';
      })

      .addCase(removeFromCart.pending, (state) => {
        state.removeLoading = true;
        state.removeSuccess = false;
        state.error = null;
      })
      .addCase(removeFromCart.fulfilled, (state, action) => {
        state.removeLoading = false;
        state.removeSuccess = true;

        if (action.payload.type == 'user') {
          const itemId = action.payload.data;
          state.cartItems = state.cartItems.filter(item => item.id !== itemId);
          state.itemCount = state.cartItems.length;
        } else {
          const data = action.payload.data;
          state.cartItems = data.items;
          state.itemCount = data.total_count;
          state.subtotal = data.subtotal;
          state.total = data.total_amount;
        }
      })
      .addCase(removeFromCart.rejected, (state, action) => {
        state.removeLoading = false;
        state.removeSuccess = false;
        state.error = action.payload || 'Failed to remove from cart';
      })

      .addCase(clearCart.pending, (state) => {
        state.loading = true;
        state.clearSuccess = false;
        state.error = null;
      })
      .addCase(clearCart.fulfilled, (state) => {
        state.loading = false;
        state.clearSuccess = true;
        state.cartItems = [];
        state.itemCount = 0;
        state.subtotal = 0;
        state.total = 0;
      })
      .addCase(clearCart.rejected, (state, action) => {
        state.loading = false;
        state.clearSuccess = false;
        state.error = action.payload || 'Failed to clear cart';
      })

      .addCase(mergeCartAfterLogin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(mergeCartAfterLogin.fulfilled, (state) => {
        state.loading = false;
        state.cartType = 'user';
      })
      .addCase(mergeCartAfterLogin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to merge cart';
      });
  }
});

export const {
  optimisticAddToCart,
  optimisticUpdateQuantity,
  optimisticRemoveItem,
  optimisticClearCart,
  calculateCartSummary,
  clearCartError,
  resetCartState,
  resetAddSuccess,
  resetUpdateSuccess,
  resetRemoveSuccess,
  resetClearSuccess,
  resetCouponSuccess,
  setTaxRate,
  setShipping,
  removeCouponLocal,
  setCartType,
  clearGuestCartState
} = cartSlice.actions;

export default cartSlice.reducer;